import React, { useEffect, useState } from 'react';
import './product-details-footer.scss';
import { LoadingSpinner, SVGS } from '@zonofi/zono-money-design-system';
import { useAppSelector } from '@zonofi/zono-money-helpers';
import {
  dispatch,
  getCustomerInvite,
  getIsSimulation,
  getIsOrderTaskEnabled,
  ProductVariantCategory,
  updateCurrentTooltipIndex,
} from '@zonofi/zono-money-store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onClickRaiseRequest } from '../utilities';

interface ProductFooterProps {
  onDecrement: (
    selectedLine: any,
    sellerWorkspaceId: string,
    customerId: string
  ) => any;
  onIncrement: (
    selectedLine: any,
    sellerWorkspaceId: string,
    customerId: string
  ) => any;
  onChangeQuantity: (
    selectedLine: any,
    qty: number,
    sellerWorkspaceId: string,
    customerId: string
  ) => any;
  onAddToCart: (val: { productVariantId: number; minOrderQty: number }) => any;
  productDetails: {
    title: string;
    price: number;
    tags: ProductVariantCategory[];
    description: string;
    details: {
      ptr: string;
      margin: string;
      moq: number;
    };
    images: string[];
    itemInCart?: {
      quantity: number;
      orderId: number;
    };
    stock: number;
    productVariantId: number;
  };
  isLoading: boolean;
  isMoqError?: boolean;
  onDelete: (
    selectedLine: any,
    sellerWorkspaceId: string,
    customerId: string
  ) => void;
  addedToCart: boolean;
  showOutOfStack: boolean;
}

export const ProductFooter: React.FC<ProductFooterProps> = ({
  onAddToCart,
  onChangeQuantity,
  onDecrement,
  onIncrement,
  productDetails,
  isLoading,
  isMoqError,
  onDelete,
  addedToCart,
  showOutOfStack,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hideAddToCart = searchParams?.get('hideAddToCart') === 'true';
  const orderId = searchParams?.get('orderId');
  const inviteData = useAppSelector(getCustomerInvite);
  const isSimulation = useAppSelector(getIsSimulation);
  const isOrderTaskEnabled = useAppSelector(getIsOrderTaskEnabled);
  const workspaceId = inviteData?.seller?.workspaceId;
  const customerId = inviteData?.inviteId;
  const { waConfig } = inviteData;
  const [qty, setQty] = useState<string>('0');

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQty(e?.target?.value);
  };

  useEffect(() => {
    setQty(productDetails?.itemInCart?.quantity?.toString() || '0');
  }, [productDetails]);

  useEffect(() => {
    if (isMoqError) {
      setQty(productDetails?.details?.moq.toString());
    }
  }, [isMoqError, productDetails]);

  return (
    <footer className="product-details-footer">
      {addedToCart && (
        <div className="product-details-footer-added-to-cart font-primary">
          <div className="product-details-footer-added-to-cart-box font-primary">
            <img src={SVGS.CheckGreenIcon} alt="added-to-cart" />
            Added To Cart
          </div>
        </div>
      )}
      <button
        className="product-details-footer-query-button"
        onClick={() => {
          onClickRaiseRequest({
            title: productDetails?.title,
            whatsappPhoneNo: waConfig?.whatsappPhoneNo,
            workspaceId: workspaceId,
          });
        }}
      >
        <img src={SVGS.WhatsAppIconWhite} alt="" />
        <div className="product-details-footer-query-button-text">
          <span className="product-details-footer-query-button-text-small">
            Any query?
          </span>
          <span className="product-details-footer-query-button-text-big font-primary-semibold">
            Chat With Us
          </span>
        </div>
      </button>
      {!hideAddToCart &&
        isOrderTaskEnabled &&
        (productDetails?.itemInCart?.quantity ? (
          <button className="product-details-footer-quantity-input">
            <img
              src={SVGS.MinusIconWhite}
              onClick={() =>
                onDecrement(
                  {
                    productVariantId: productDetails?.productVariantId,
                    quantity: productDetails?.itemInCart?.quantity,
                    orderId: productDetails?.itemInCart?.orderId,
                  },
                  workspaceId,
                  customerId
                )
              }
            />
            {isLoading ? (
              <div className="product-details-footer-quantity-input-loader">
                <LoadingSpinner size={15} />
              </div>
            ) : (
              <input
                type="number"
                value={qty}
                onChange={(e) => onChange(e)}
                onBlur={() => {
                  onChangeQuantity(
                    {
                      productVariantId: productDetails?.productVariantId,
                      quantity: productDetails?.itemInCart?.quantity,
                      orderId: productDetails?.itemInCart?.orderId,
                    },
                    Number(qty),
                    workspaceId,
                    customerId
                  );
                }}
              />
            )}
            <img
              src={SVGS.PlusIconWhite}
              onClick={() =>
                onIncrement(
                  {
                    productVariantId: productDetails?.productVariantId,
                    quantity: productDetails?.itemInCart?.quantity,
                    orderId: productDetails?.itemInCart?.orderId,
                  },
                  workspaceId,
                  customerId
                )
              }
            />
          </button>
        ) : (
          <button
            className="product-details-footer-add-to-cart-button"
            onClick={() =>
              onAddToCart({
                productVariantId: productDetails?.productVariantId,
                minOrderQty: productDetails?.details?.moq,
              })
            }
            disabled={showOutOfStack && productDetails?.stock < 1}
          >
            <img src={SVGS.CartIconWhite} alt="" />
            {isLoading ? (
              <LoadingSpinner size={15} />
            ) : orderId ? (
              'Add To Order'
            ) : (
              'Add To Cart'
            )}
          </button>
        ))}
      {!hideAddToCart &&
        isOrderTaskEnabled &&
        productDetails?.itemInCart?.quantity && (
          // <div
          //   className="delete-product"
          //   onClick={() =>
          //     onDelete(
          //       {
          //         productVariantId: productDetails?.productVariantId,
          //         quantity: productDetails?.itemInCart?.quantity,
          //         orderId: productDetails?.itemInCart?.orderId,
          //       },
          //       workspaceId,
          //       customerId
          //     )
          //   }
          // >
          //   <img src={SVGS.DeleteRed} alt="" />
          // </div>
          <div
            className="product-details-footer-cart-button"
            onClick={() => {
              if (isSimulation) {
                dispatch(updateCurrentTooltipIndex(2));
              }
              navigate('/cart');
            }}
          >
            <img src={SVGS.AddedToCart} />
          </div>
        )}
    </footer>
  );
};
